<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import type { ActiveSwitchTest, VpnKey } from '@/types'
import QrModal from '@/components/keys/QrModal.vue'
import KeyConfigModal from '@/components/keys/KeyConfigModal.vue'
import ManualKeyRow from '@/components/keys/ManualKeyRow.vue'
import LocationDisplay from '@/components/ui/LocationDisplay.vue'
import { useSwitchStore } from '@/stores/switch'
import { formatDateTime, formatTimer } from '@/utils/format'
import { expiryBadge, keyIdentifier } from '@/utils/keys'
import { splitKeysForSwitch } from '@/utils/switch'
import SwitchProgressBar from '@/components/switch/SwitchProgressBar.vue'

const props = defineProps<{ keys: VpnKey[]; test: ActiveSwitchTest }>()

const switchStore = useSwitchStore()
const test = computed(() => props.test)

const firstKey = computed(() => props.keys[0])
const badge = computed(() => expiryBadge(firstKey.value))
const split = computed(() => (test.value ? splitKeysForSwitch(props.keys, test.value) : { oldKeys: [], newKeys: [] }))

const now = ref(Date.now())
let tickTimer: ReturnType<typeof setInterval> | null = null

const secondsLeft = computed(() => {
  if (!test.value) return 0
  return Math.max(0, Math.floor((test.value.expiresAtMs - now.value) / 1000))
})
const timerLabel = computed(() => formatTimer(secondsLeft.value))
const progress = computed(() => Math.max(0, Math.min(100, (secondsLeft.value / 3600) * 100)))
const groupKey = computed(() => test.value?.switchGroupKey || '')
const hungText = computed(() => (groupKey.value ? switchStore.hungMessages[groupKey.value] : null) ?? null)
const isExpiredOrHung = computed(() =>
  hungText.value !== null
  || test.value?.status === 'hung'
  || secondsLeft.value <= 0,
)
const confirming = computed(() => !!(test.value && switchStore.confirming[test.value.switchId]))
const cancelling = computed(() => !!(test.value && switchStore.cancelling[test.value.switchId]))

const qrOpen = ref(false)
const qrData = ref('')
const qrTitle = ref('')
const keyOpen = ref(false)
const keyConfig = ref('')
const keyTitle = ref('')

function openQr(key: VpnKey) {
  qrData.value = key.qr_data || key.config_string || ''
  qrTitle.value = key.server_name
  qrOpen.value = true
}

function openKey(key: VpnKey) {
  keyConfig.value = key.config_string || ''
  keyTitle.value = key.server_name
  keyOpen.value = true
}

function startTick() {
  stopTick()
  now.value = Date.now()
  tickTimer = setInterval(() => {
    now.value = Date.now()
    if (test.value && secondsLeft.value <= 0 && !isExpiredOrHung.value) {
      void switchStore.poll()
    }
  }, 1000)
}

function stopTick() {
  if (tickTimer) {
    clearInterval(tickTimer)
    tickTimer = null
  }
}

watch(
  () => test.value?.expiresAtMs,
  () => startTick(),
  { immediate: true },
)

onMounted(() => startTick())
onUnmounted(() => {
  stopTick()
})

async function confirmSwitch() {
  if (!test.value) return
  const res = await switchStore.confirmSwitch(test.value.switchId)
  if (!res.success) alert(res.message || 'Не удалось подтвердить смену')
}

async function cancelSwitch() {
  if (!test.value) return
  if (!confirm('Отменить смену? Новый сервер будет заблокирован для вас.')) return
  const res = await switchStore.cancelSwitch(test.value.switchId)
  if (!res.success) alert(res.message || 'Не удалось отменить смену')
}
</script>

<template>
  <div
    v-if="test && firstKey"
    class="panel mb-3 overflow-hidden border-2 border-warning !p-0"
  >
    <div class="border-b border-warning/30 bg-warning/10 px-4 py-3">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 class="text-sm font-bold text-warning">
            <template v-if="isExpiredOrHung">Завершение смены сервера</template>
            <template v-else>Тестирование смены сервера</template>
          </h3>
          <p class="mt-1 text-xs text-muted">
            <template v-if="isExpiredOrHung">
              Дождитесь автоматического завершения смены...
            </template>
            <template v-else>
              У вас есть 60 минут на проверку. Если не подходит — отмените смену.
            </template>
          </p>
        </div>
        <div class="text-end">
          <div class="text-xl font-extrabold" :class="isExpiredOrHung ? 'text-warning' : ''">
            <template v-if="isExpiredOrHung">Завершение...</template>
            <template v-else>{{ timerLabel }}</template>
          </div>
          <div class="mt-1 w-full min-w-[10rem] max-w-xs">
            <SwitchProgressBar :progress="isExpiredOrHung ? 0 : progress" />
          </div>
        </div>
      </div>
      <div class="mt-2 text-xs text-muted">
        <strong>ключ:</strong> {{ keyIdentifier(firstKey) }}
        <span class="text-muted"> · </span>
        <strong>Срок действия:</strong> {{ formatDateTime(firstKey.expires_at) }}
        <span :class="badge.className" class="ml-1">{{ badge.text }}</span>
      </div>
    </div>

    <div class="space-y-4 p-4">
      <div class="border-b border-danger/25 pb-4">
        <div class="mb-2 flex flex-wrap items-center gap-2 text-sm">
          <span class="badge badge-danger">Старый сервер</span>
          <strong>{{ test.oldServerName }}</strong>
          <span class="text-xs text-muted">
            (<LocationDisplay
              :location="test.oldServerLocation"
              inline
              :size="16"
            />)
          </span>
          <span class="ml-auto text-xs text-danger">Будет удалён при подтверждении</span>
        </div>
        <ManualKeyRow
          v-for="key in split.oldKeys"
          :key="key.id"
          :key-item="key"
          @show-key="openKey"
          @show-qr="openQr"
        />
        <div v-if="split.oldKeys.length === 0" class="py-2 text-xs text-muted">
          Нет ключей на старом сервере для этой группы
        </div>
      </div>

      <div>
        <div class="mb-2 flex flex-wrap items-center gap-2 text-sm">
          <span class="badge badge-success">Новый сервер</span>
          <strong>{{ test.newServerName }}</strong>
          <span class="text-xs text-muted">
            (<LocationDisplay
              :location="test.newServerLocation"
              inline
              :size="16"
            />)
          </span>
          <span class="ml-auto text-xs text-success">Останется после подтверждения</span>
        </div>
        <ManualKeyRow
          v-for="key in split.newKeys"
          :key="key.id"
          :key-item="key"
          @show-key="openKey"
          @show-qr="openQr"
        />
        <div v-if="split.newKeys.length === 0" class="py-2 text-xs text-muted">
          <span class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-warning border-t-transparent" />
          Создание ключей на новом сервере... Страница обновится автоматически.
        </div>
      </div>
    </div>

    <div class="border-t border-warning/30 bg-warning/10 px-4 py-3">
      <div v-if="hungText" class="alert alert-danger mb-0 py-2 text-center text-xs">
        {{ hungText }}
      </div>
      <div v-else-if="isExpiredOrHung" class="alert alert-warning mb-0 py-2 text-center text-xs">
        Дождитесь автоматического завершения смены. Страница обновится автоматически.
      </div>
      <div v-else class="flex flex-wrap items-center justify-between gap-3">
        <span class="text-xs text-muted">Оба сервера доступны во время тестового периода</span>
        <div class="flex gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="cancelling || confirming"
            @click="cancelSwitch"
          >
            {{ cancelling ? 'Отмена…' : 'Отменить смену' }}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            :disabled="confirming || cancelling"
            @click="confirmSwitch"
          >
            {{ confirming ? 'Подтверждение…' : 'Подтвердить смену' }}
          </button>
        </div>
      </div>
    </div>

    <QrModal :open="qrOpen" :data="qrData" :title="qrTitle" @close="qrOpen = false" />
    <KeyConfigModal :open="keyOpen" :config="keyConfig" :title="keyTitle" @close="keyOpen = false" />
  </div>
</template>
